// Phygital · Functional "Plan a Makeup Experience" page.
// This is the NEW build (matches Figma). Reads catalog from Airtable via
// /api/phygital/catalog, saves through /api/user/experiences (so the existing
// SharePreferenceCardPage picks it up), then auto-mints a share_id and
// navigates to /share/card/{share_id}.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, ChevronDown, Clock, DollarSign, Edit, Eye, MapPin, Paintbrush,
  Plus, Save, Trash2, Upload, User,
} from "lucide-react";
import {
  TOKENS, CATEGORY_COLORS, Tag, Btn, SegButton, SegRow, Pill, TextInput,
  FieldLabel, HintText, Collapsible, VeinHalf, OptionTile, HostRow,
  InlineNote, CloseButton,
} from "./primitives";
import SignInModal from "@/components/SignInModal";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import AdminBar from "./AdminBar";
import { trackEvent } from "./hooks/useTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ---- eye/lash svg mini-visuals (used inside preview card) ----
const EyeSvg = ({ shape = "almond" }) => {
  const p = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (shape === "round")      return <svg viewBox="0 0 40 24" className="w-8 h-5"><ellipse cx="20" cy="12" rx="14" ry="9" {...p} /><circle cx="20" cy="12" r="3" fill="currentColor" /></svg>;
  if (shape === "hooded")     return <svg viewBox="0 0 40 24" className="w-8 h-5"><path d="M4 14 C 10 8 30 8 36 14" {...p} /><path d="M4 14 C 12 20 28 20 36 14" {...p} /><circle cx="20" cy="14" r="2.5" fill="currentColor" /></svg>;
  if (shape === "monolid")    return <svg viewBox="0 0 40 24" className="w-8 h-5"><path d="M4 12 L 36 12" {...p} /><path d="M6 15 C 14 20 26 20 34 15" {...p} /><circle cx="20" cy="13" r="2.5" fill="currentColor" /></svg>;
  if (shape === "downturned") return <svg viewBox="0 0 40 24" className="w-8 h-5"><path d="M4 8 C 12 12 28 16 36 20" {...p} /><path d="M4 10 C 14 20 30 20 36 18" {...p} /><circle cx="18" cy="14" r="2.5" fill="currentColor" /></svg>;
  if (shape === "upturned")   return <svg viewBox="0 0 40 24" className="w-8 h-5"><path d="M4 18 C 12 14 28 10 36 6" {...p} /><path d="M4 16 C 14 6 30 6 36 8" {...p} /><circle cx="22" cy="12" r="2.5" fill="currentColor" /></svg>;
  return <svg viewBox="0 0 40 24" className="w-8 h-5"><path d="M4 12 C 12 4 28 4 36 12 C 28 20 12 20 4 12 Z" {...p} /><circle cx="20" cy="12" r="3" fill="currentColor" /></svg>;
};

function useCatalog() {
  const [c, setC] = useState(null);
  useEffect(() => { axios.get(`${API}/phygital/catalog`).then(r => setC(r.data)).catch(() => setC({})); }, []);
  return c;
}
function useMe() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const t = localStorage.getItem("session_token");
    if (!t) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(r => setMe(r.data)).catch(() => setMe(null)).finally(() => setLoading(false));
  }, []);
  return { me, loading };
}

export default function PhygitalPlanMakeup() {
  const navigate = useNavigate();
  const catalog = useCatalog();
  const { me, loading: authLoading } = useMe();
  const CATEGORY = "makeup";
  const CAT = CATEGORY_COLORS[CATEGORY];

  // Flow state
  const [service, setService] = useState(null);
  const [occasionId, setOccasionId] = useState("");
  const [whoMode, setWhoMode] = useState("tbd");   // tbd | diy | host
  const [whoHostId, setWhoHostId] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [hostsOpen, setHostsOpen] = useState(false);
  const [hostFormOpen, setHostFormOpen] = useState(false);
  const [whereMode, setWhereMode] = useState("tbd_city");
  const [whereCity, setWhereCity] = useState("");
  const [whereAddress, setWhereAddress] = useState("");
  const [whenMode, setWhenMode] = useState("tbd");
  const [whenStartsAt, setWhenStartsAt] = useState("");
  const [budgetService, setBudgetService] = useState(0);
  const [budgetProducts, setBudgetProducts] = useState(0);

  const [prefsOpen, setPrefsOpen] = useState(false);
  const [savedPrefs, setSavedPrefs] = useState({});
  const [inspoOpen, setInspoOpen] = useState(false);
  const [inspoImages, setInspoImages] = useState([]);

  const [saving, setSaving] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  const refreshHosts = useCallback(async () => {
    if (!localStorage.getItem("session_token")) return;
    try { const r = await axios.get(`${API}/phygital/me/hosts`, { headers: authHeaders() }); setHosts(r.data.hosts || []); } catch { /* ignore */ }
  }, []);
  const refreshPrefs = useCallback(async () => {
    if (!localStorage.getItem("session_token")) return;
    try { const r = await axios.get(`${API}/phygital/me/preferences`, { headers: authHeaders() }); setSavedPrefs(r.data.phygital || {}); } catch { /* ignore */ }
  }, []);
  const refreshInspo = useCallback(async () => {
    if (!localStorage.getItem("session_token")) return;
    try { const r = await axios.get(`${API}/phygital/me/inspo?category=${CATEGORY}`, { headers: authHeaders() }); setInspoImages(r.data.inspo || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { refreshHosts(); refreshPrefs(); refreshInspo(); }, [refreshHosts, refreshPrefs, refreshInspo, me]);

  const servicePills = (catalog?.service_pills || {})[CATEGORY] || ["Full Glam", "Soft Glam", "Touch-Up", "Bridal Trial"];
  const occasions = catalog?.occasions || [];
  const pickedHost = hosts.find(h => h.host_id === whoHostId);

  useEffect(() => {
    if (whoMode === "host" && pickedHost && whereMode === "at_host") setWhereAddress(pickedHost.address || "");
  }, [whoMode, pickedHost, whereMode]);

  const prefsCount = useMemo(() => {
    const m = savedPrefs?.makeup || {};
    return Object.values(m).filter(v => (Array.isArray(v) ? v.length : !!v && typeof v !== "object")).length;
  }, [savedPrefs]);

  const handleSave = async () => {
    if (!localStorage.getItem("session_token")) { toast.error("Please sign in first."); return; }
    if (!service) { toast.error("Pick a service before saving."); return; }
    setSaving(true);
    try {
      const occName = occasions.find(o => o.occasion_id === occasionId)?.name || "";
      const universal = savedPrefs?.universal || {};
      const makeup = savedPrefs?.makeup || {};
      const body = {
        category: CATEGORY,
        name: `${service}${occName ? ` - ${occName}` : ""}`,
        type: "individual",
        target_date: whenMode === "pick" ? (whenStartsAt || null) : null,
        provider_status: whoMode === "host" ? "confirmed" : (whoMode === "diy" ? "diy" : "tbd"),
        provider_name: pickedHost?.name || null,
        provider_phone: pickedHost?.phone || null,
        occasion: occasionId || null,
        notes: "",
        services: [{ name: service, category: CATEGORY }],
        defaults: { ...makeup, universal, sub_event: null, service, who: whoMode, where: whereMode, city: whereCity, address: whereAddress },
        preferences_snapshot: { ...makeup, universal },
        inspo: inspoImages.map(i => ({ url: i.url, inspo_id: i.inspo_id })),
        inspiration_ids: inspoImages.map(i => i.inspo_id),
        status: "planning",
      };
      const r = await axios.post(`${API}/user/experiences`, body, { headers: authHeaders() });
      const expId = r.data?.experience?.experience_id;
      trackEvent("plan.experience.saved", { experience_id: expId, category: CATEGORY, service, occasion_id: occasionId }, { surface: "plan_makeup" });
      if (expId) {
        try {
          const shr = await axios.post(`${API}/user/experiences/${expId}/share`, {}, { headers: authHeaders() });
          const shareId = shr.data?.share_id;
          if (shareId) { navigate(`/share/card/${shareId}`); return; }
        } catch { /* fall through */ }
      }
      toast.success("Experience saved");
      navigate("/me");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // --- signed-out gate ---
  if (authLoading) {
    return <div className={TOKENS.PAGE}><div className={TOKENS.FRAME}><p className="text-center py-10 text-sm text-stone-500">Loading...</p></div></div>;
  }
  if (!me) {
    return (
      <div className={TOKENS.PAGE}>
        <div className={TOKENS.FRAME}>
          <div className="px-6 py-10 space-y-4 text-center">
            <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ backgroundColor: CAT.soft }}>
              <Paintbrush className="w-6 h-6" style={{ color: CAT.hex }} />
            </div>
            <h2 className="text-lg font-bold text-stone-900">Sign in to save your plan</h2>
            <Btn tone="ink" full icon={User} onClick={() => setShowSignIn(true)} testid="signin-cta">Sign in</Btn>
            <Btn tone="outline" full onClick={() => navigate("/")} testid="back-home">Back home</Btn>
          </div>
        </div>
        <SignInModal open={showSignIn} onOpenChange={setShowSignIn} redirectUrl="/plan/makeup" />
      </div>
    );
  }

  return (
    <div className={TOKENS.PAGE} data-testid="phygital-plan-makeup">
      {/* Header - matches mockup: back arrow, title, subtitle, COMPONENTS link */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-stone-200 px-4 h-14 flex items-center justify-between">
        <button onClick={() => navigate("/me")} className="w-8 h-8 -ml-1 flex items-center justify-center text-stone-700 hover:bg-stone-100 rounded-full" data-testid="back-to-dashboard" aria-label="Back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-center flex-1">
          <p className="text-sm font-bold text-stone-900 leading-tight">Plan Your Makeup Experience</p>
          {me && <p className="text-[9px] uppercase tracking-wider text-stone-500 mt-0.5">Signed in as {me.name || me.email}</p>}
        </div>
        <Link to="/design/components" className="text-[9px] font-bold text-purple-700 uppercase tracking-wider" data-testid="link-components">Components</Link>
      </header>

      <div className={TOKENS.FRAME}>
        <div className="px-4 pt-4 pb-24 space-y-5">
          {/* Category ribbon */}
          <div className="rounded-xl p-3 flex items-center gap-2" style={{ backgroundColor: CAT.soft }} data-testid="category-ribbon">
            <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center"><Paintbrush className="w-3 h-3" style={{ color: CAT.hex }} /></div>
            <span className="text-sm font-semibold text-stone-900 flex-1">Makeup</span>
            <Tag tone="accent" size="xs">{whoMode === "diy" ? "DIY plan" : "Share with your host"}</Tag>
          </div>

          {/* WHO */}
          <div>
            <FieldLabel right={whoMode === "diy" ? null : (
              <button className="text-[10px] text-purple-700 font-semibold inline-flex items-center gap-1" onClick={() => setHostsOpen(!hostsOpen)} data-testid="my-hosts-btn">
                <User className="w-3 h-3" /> My hosts ({hosts.length}) <ChevronDown className={`w-3 h-3 transition ${hostsOpen ? "rotate-180" : ""}`} />
              </button>
            )}>Who is doing it?</FieldLabel>
            <SegRow>
              <SegButton Icon={User} selected={whoMode === "diy"} onClick={() => { setWhoMode("diy"); setWhoHostId(null); }} testid="who-diy">DIY</SegButton>
              <SegButton Icon={Clock} selected={whoMode === "tbd"} onClick={() => { setWhoMode("tbd"); setWhoHostId(null); }} testid="who-tbd">TBD</SegButton>
              {pickedHost ? (
                <button onClick={() => setHostsOpen(true)} data-testid="who-picked" className="flex-1 h-10 min-w-0 rounded-full bg-stone-900 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 px-4 whitespace-nowrap">
                  <span className="w-3 h-3 rounded-full bg-white inline-block shrink-0" /><span className="truncate">{pickedHost.name}</span>
                </button>
              ) : (
                <SegButton Icon={Plus} onClick={() => setHostsOpen(true)} testid="who-add-host">Host</SegButton>
              )}
            </SegRow>
            {whoMode === "diy" && <InlineNote tone="info">DIY mode: skip the location, save just for yourself.</InlineNote>}
            {hostsOpen && (
              <div className="mt-2 rounded-2xl border border-stone-200 bg-white p-3 space-y-2" data-testid="hosts-dropdown">
                {hosts.length === 0 && <p className="text-[11px] text-stone-500 text-center py-2">No hosts yet. Add your first.</p>}
                {hosts.map(h => (
                  <HostRow key={h.host_id} name={h.name} brand={h.brand} selected={whoHostId === h.host_id}
                    onClick={() => { setWhoMode("host"); setWhoHostId(h.host_id); setHostsOpen(false); }} testid={`host-${h.host_id}`} />
                ))}
                <button onClick={() => setHostFormOpen(true)} data-testid="open-add-host" className="w-full rounded-xl border border-dashed border-stone-300 px-3 py-2.5 flex items-center justify-center gap-2 text-xs font-semibold text-stone-600 hover:border-stone-900">
                  <Plus className="w-3.5 h-3.5" /> Add new host
                </button>
              </div>
            )}
          </div>

          {/* WHERE (hidden if DIY) */}
          {whoMode !== "diy" && (
            <div data-testid="where-section">
              <FieldLabel>Where?</FieldLabel>
              <SegRow>
                <SegButton selected={whereMode === "tbd_city"} onClick={() => setWhereMode("tbd_city")} testid="where-tbd">TBD - just city</SegButton>
                <SegButton selected={whereMode === "at_host"} onClick={() => setWhereMode("at_host")} testid="where-host">At host</SegButton>
                <SegButton selected={whereMode === "travel"} onClick={() => setWhereMode("travel")} testid="where-travel">Travel to me</SegButton>
              </SegRow>
              {whereMode === "tbd_city" && (
                <div className="mt-2">
                  <FieldLabel><span className="inline-flex items-center gap-1 text-stone-700 font-semibold text-[11px]"><MapPin className="w-3 h-3 text-stone-500" /> City, State, Country</span></FieldLabel>
                  <GooglePlacesAutocomplete value={whereCity} onInputChange={setWhereCity} onChange={(p) => setWhereCity(p?.address || p?.name || whereCity)} placeholder="Los Angeles, CA" />
                  <HintText>Just the city, not a full address. We&apos;ll narrow it down with your host.</HintText>
                </div>
              )}
              {whereMode === "at_host" && (
                <div className="mt-2 rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs" data-testid="where-host-address">
                  <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold mb-1">Host&apos;s address</p>
                  <p className="text-stone-900">{whereAddress || pickedHost?.address || "Pick a host above first."}</p>
                </div>
              )}
              {whereMode === "travel" && (
                <div className="mt-2 space-y-2">
                  <GooglePlacesAutocomplete value={whereAddress} onInputChange={setWhereAddress} onChange={(p) => setWhereAddress(p?.address || p?.name || whereAddress)} placeholder="Full address" />
                </div>
              )}
            </div>
          )}

          {/* WHEN */}
          <div>
            <FieldLabel>When?</FieldLabel>
            <SegRow>
              <SegButton Icon={Clock} selected={whenMode === "tbd"} onClick={() => setWhenMode("tbd")} testid="when-tbd">Date TBD</SegButton>
              <SegButton Icon={Clock} selected={whenMode === "pick"} onClick={() => setWhenMode("pick")} testid="when-pick">Pick date & time</SegButton>
            </SegRow>
            {whenMode === "pick" && <div className="mt-2"><TextInput type="datetime-local" value={whenStartsAt} onChange={setWhenStartsAt} testid="when-datetime" /></div>}
          </div>

          {/* OCCASION */}
          <div>
            <FieldLabel>What&apos;s the occasion? (optional)</FieldLabel>
            <div className="relative">
              <select data-testid="occasion-dropdown" value={occasionId} onChange={(e) => setOccasionId(e.target.value)} className="w-full appearance-none rounded-xl border border-stone-300 bg-white text-sm text-stone-900 px-3 py-2.5 pr-8 font-medium focus:outline-none focus:border-stone-900">
                <option value="">Choose an occasion...</option>
                {occasions.map(o => <option key={o.occasion_id} value={o.occasion_id}>{o.name}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 text-stone-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* LOOK */}
          <div>
            <h3 className="text-sm font-bold text-stone-900 mb-2">What is the look?</h3>
            <p className="text-[11px] font-semibold text-stone-700 mb-1.5">Service</p>
            <div className="flex flex-wrap gap-1.5">
              {servicePills.map(s => (
                <Pill key={s} selected={service === s} onClick={() => setService(s)} testid={`service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}>{s}</Pill>
              ))}
            </div>

            {/* PREFERENCES preview card */}
            <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-3" data-testid="prefs-card">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-purple-600 text-sm">✦</span>
                <p className="text-xs font-bold text-stone-900">Preferences</p>
                <Tag tone="outline" size="xs">({prefsCount})</Tag>
                <button onClick={() => setPrefsOpen(true)} data-testid="open-prefs" className="ml-auto text-[10px] font-bold text-stone-700 uppercase tracking-wider inline-flex items-center gap-1 hover:text-stone-900">
                  <Edit className="w-3 h-3" /> Edit
                </button>
              </div>
              <PrefsPreviewMini catalog={catalog} makeup={savedPrefs?.makeup || {}} onEdit={() => setPrefsOpen(true)} />
            </div>

            {/* INSPO card */}
            <div className="mt-2 rounded-2xl border border-stone-200 bg-white p-3" data-testid="inspo-card">
              <div className="flex items-center gap-1.5 mb-2">
                <Camera className="w-3.5 h-3.5 text-stone-600" />
                <p className="text-xs font-bold text-stone-900">Inspiration</p>
                <Tag tone="outline" size="xs">({inspoImages.length})</Tag>
                <button onClick={() => setInspoOpen(true)} data-testid="open-inspo" className="ml-auto text-[10px] font-bold text-stone-700 uppercase tracking-wider inline-flex items-center gap-1 hover:text-stone-900">
                  <Edit className="w-3 h-3" /> Edit
                </button>
              </div>
              {inspoImages.length === 0 ? (
                <button onClick={() => setInspoOpen(true)} data-testid="inspo-empty" className="w-full rounded-xl border-2 border-dashed border-stone-300 hover:border-purple-500 h-20 flex flex-col items-center justify-center gap-1 text-stone-500">
                  <Upload className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-wider font-semibold">Upload your inspo</span>
                </button>
              ) : (
                <div className="grid grid-cols-5 gap-1.5">
                  {inspoImages.slice(0, 5).map(i => (
                    <img key={i.inspo_id} src={i.url.startsWith("http") ? i.url : `${process.env.REACT_APP_BACKEND_URL}${i.url}`} alt="inspo" className="aspect-square object-cover rounded-lg" />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* BUDGET */}
          <div>
            <h3 className="text-sm font-bold text-stone-900 mb-2">Budget (optional)</h3>
            <div className="grid grid-cols-2 gap-2">
              {whoMode !== "diy" && (
                <div>
                  <p className="text-[11px] text-stone-600 mb-1">Service ($)</p>
                  <div className="rounded-xl border border-stone-300 flex items-center px-3 py-2">
                    <DollarSign className="w-3.5 h-3.5 text-stone-500" />
                    <input type="number" value={budgetService} onChange={(e) => setBudgetService(e.target.value)} className="flex-1 bg-transparent text-sm focus:outline-none ml-1" data-testid="budget-service" />
                  </div>
                </div>
              )}
              <div className={whoMode === "diy" ? "col-span-2" : ""}>
                <p className="text-[11px] text-stone-600 mb-1">Products ($)</p>
                <div className="rounded-xl border border-stone-300 flex items-center px-3 py-2">
                  <DollarSign className="w-3.5 h-3.5 text-stone-500" />
                  <input type="number" value={budgetProducts} onChange={(e) => setBudgetProducts(e.target.value)} className="flex-1 bg-transparent text-sm focus:outline-none ml-1" data-testid="budget-products" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky footer: eye icon + Save & share */}
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 px-4 py-3 flex gap-2 items-center" style={{ maxWidth: TOKENS.MAX_W, left: "50%", transform: "translateX(-50%)" }}>
          <button className="w-10 h-10 rounded-xl border border-stone-300 flex items-center justify-center hover:border-stone-900" data-testid="preview-card" title="Preview card">
            <Eye className="w-4 h-4 text-stone-700" />
          </button>
          <div className="flex-1">
            <Btn full tone="ink" icon={saving ? Clock : Save} onClick={handleSave} disabled={!service || saving} testid="save-experience">
              {saving ? "Saving..." : (whoMode === "diy" ? "Save DIY plan" : "Save & share with host")}
            </Btn>
          </div>
        </div>
      </div>

      {prefsOpen && (
        <PreferencesDrawer catalog={catalog} savedPrefs={savedPrefs} onClose={() => setPrefsOpen(false)} onSaved={refreshPrefs} />
      )}
      {inspoOpen && <InspoDrawer inspo={inspoImages} onClose={() => setInspoOpen(false)} onSaved={refreshInspo} />}
      {hostFormOpen && (
        <HostFormDrawer onClose={() => setHostFormOpen(false)} onSaved={async (created) => {
          await refreshHosts();
          setWhoMode("host"); setWhoHostId(created.host_id); setHostFormOpen(false); setHostsOpen(false);
        }} />
      )}
      <AdminBar />
    </div>
  );
}

// ---- Preferences preview mini (used in the card above) ----
function PrefsPreviewMini({ catalog, makeup, onEdit }) {
  const options = catalog?.pref_options || {};
  const findOpt = (fieldId, optionId) => (options[fieldId] || []).find(o => o.option_id === optionId);
  const findOpts = (fieldId, optionIds = []) => (optionIds || []).map(id => findOpt(fieldId, id)).filter(Boolean);

  const undertoneOpt = makeup?.undertone ? findOpt("undertone", makeup.undertone) : null;
  const eyeOpt = makeup?.eye_shape ? findOpt("eye_shape", makeup.eye_shape) : null;
  const faveBrands = Array.isArray(makeup?.fave_brands) ? makeup.fave_brands : [];
  const heroProducts = Array.isArray(makeup?.hero_products) ? makeup.hero_products : [];

  const anything = undertoneOpt || eyeOpt || faveBrands.length || heroProducts.length;
  if (!anything) {
    return (
      <button onClick={onEdit} data-testid="prefs-empty" className="w-full rounded-xl border-2 border-dashed border-stone-300 hover:border-purple-500 py-4 text-center">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">Select to build your look</p>
      </button>
    );
  }

  return (
    <div className="divide-y divide-stone-100">
      {(undertoneOpt || eyeOpt) && (
        <div className="flex items-start gap-2 py-1" data-testid="preview-canvas">
          <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold w-16 shrink-0 pt-0.5">Canvas</p>
          <div className="flex-1 flex flex-wrap items-center gap-1.5">
            {undertoneOpt && (
              <span className="inline-flex items-center gap-1.5 bg-stone-50 border border-stone-200 rounded-full pl-0.5 pr-2 py-0.5">
                <span className="w-6 h-3 rounded-full overflow-hidden inline-flex flex-col border border-white">
                  <span className="h-1/2 w-full" style={{ backgroundColor: undertoneOpt.hex_top }} />
                  <span className="h-1/2 w-full" style={{ backgroundColor: undertoneOpt.hex_bottom }} />
                </span>
                <span className="text-[10px] font-semibold text-stone-800">{undertoneOpt.label}</span>
              </span>
            )}
            {eyeOpt && (
              <span className="inline-flex items-center gap-1 bg-stone-50 border border-stone-200 rounded-full pl-1 pr-2 py-0.5 text-stone-800">
                <EyeSvg shape={eyeOpt.option_id} />
                <span className="text-[10px] font-semibold">{eyeOpt.label}</span>
              </span>
            )}
          </div>
        </div>
      )}
      {(faveBrands.length > 0 || heroProducts.length > 0) && (
        <div className="flex items-start gap-2 py-1" data-testid="preview-products">
          <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold w-16 shrink-0 pt-0.5">Products</p>
          <div className="flex-1 flex flex-wrap items-center gap-1.5">
            {faveBrands.map((b, i) => (<span key={`b-${i}`} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{b}</span>))}
            {heroProducts.map((h, i) => (<span key={`h-${i}`} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{h}</span>))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Preferences drawer ----
function PreferencesDrawer({ catalog, savedPrefs, onClose, onSaved }) {
  const schema = catalog?.pref_schema || [];
  const options = catalog?.pref_options || {};
  const [makeup, setMakeup] = useState(savedPrefs?.makeup || {});
  const [universal, setUniversal] = useState(savedPrefs?.universal || {});
  const [saving, setSaving] = useState(false);

  const sections = { universal: [], canvas: [], look: [], faves: [], notes: [] };
  schema.forEach(f => {
    if (f.who_gate === "provider_only") return; // Vibe hidden for V1
    if (f.section === "vibe") return;
    (sections[f.section] || sections.canvas).push(f);
  });

  const isMulti = (t) => t === "pill_multi" || t === "text_multi";
  const bagFor = (section) => section === "universal" ? [universal, setUniversal] : [makeup, setMakeup];

  const toggle = (bag, setBag, fieldId, optionId, multi) => {
    setBag(prev => {
      const next = { ...prev };
      if (multi) {
        const cur = Array.isArray(next[fieldId]) ? [...next[fieldId]] : [];
        const i = cur.indexOf(optionId);
        if (i >= 0) cur.splice(i, 1); else cur.push(optionId);
        next[fieldId] = cur;
      } else {
        next[fieldId] = next[fieldId] === optionId ? "" : optionId;
      }
      return next;
    });
  };

  const renderField = (f) => {
    const [bag, setBag] = bagFor(f.section);
    const val = bag[f.field_id];
    const multi = isMulti(f.input_type);
    const opts = options[f.field_id] || [];

    if (f.input_type === "textarea") {
      return (
        <div className="space-y-1">
          <FieldLabel>{f.label}</FieldLabel>{f.hint && <HintText>{f.hint}</HintText>}
          <textarea value={val || ""} onChange={(e) => setBag(prev => ({ ...prev, [f.field_id]: e.target.value }))} data-testid={`pref-${f.field_id}`} className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:border-stone-900" />
        </div>
      );
    }
    if (f.input_type === "text_multi") {
      return <TextMultiInput f={f} val={val} setBag={setBag} />;
    }
    if (f.input_type === "pill_single" && f.field_id === "undertone") {
      return (
        <div className="space-y-1">
          <FieldLabel>{f.label}</FieldLabel><HintText>{f.hint}</HintText>
          <div className="flex gap-2">{opts.map(o => (
            <VeinHalf key={o.option_id} top={o.hex_top} bottom={o.hex_bottom} selected={val === o.option_id}
              onClick={() => toggle(bag, setBag, f.field_id, o.option_id, false)} testid={`pref-undertone-${o.option_id}`} label={o.label} />
          ))}</div>
        </div>
      );
    }
    if (f.input_type === "tile") {
      return (
        <div className="space-y-1">
          <FieldLabel>{f.label}</FieldLabel>{f.hint && <HintText>{f.hint}</HintText>}
          <div className="grid grid-cols-3 gap-2">{opts.map(o => (
            <OptionTile key={o.option_id} label={o.label} selected={val === o.option_id}
              onClick={() => toggle(bag, setBag, f.field_id, o.option_id, false)}
              testid={`pref-${f.field_id}-${o.option_id}`}
              visual={f.field_id === "eye_shape" ? <EyeSvg shape={o.option_id} /> : <div className="w-6 h-6 rounded-full bg-stone-300" />} />
          ))}</div>
        </div>
      );
    }
    const tone = f.section === "universal" ? "rose" : "cream";
    return (
      <div className="space-y-1">
        <FieldLabel>{f.label}</FieldLabel>{f.hint && <HintText>{f.hint}</HintText>}
        <div className="flex flex-wrap gap-1.5">{opts.map(o => (
          <Pill key={o.option_id} tone={tone}
            selected={multi ? (Array.isArray(val) && val.includes(o.option_id)) : val === o.option_id}
            onClick={() => toggle(bag, setBag, f.field_id, o.option_id, multi)}
            testid={`pref-${f.field_id}-${o.option_id}`}>{o.label}</Pill>
        ))}</div>
      </div>
    );
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/phygital/me/preferences`, { category: "universal", selections: universal }, { headers: authHeaders() });
      await axios.post(`${API}/phygital/me/preferences`, { category: "makeup", selections: makeup }, { headers: authHeaders() });
      toast.success("Preferences saved");
      await onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" data-testid="prefs-drawer">
      <div className={`${TOKENS.FRAME} bg-white rounded-t-3xl max-h-[92vh] overflow-y-auto pb-24 relative`}>
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between border-b border-stone-100 px-4 h-14">
          <p className="text-sm font-bold">Edit preferences</p>
          <CloseButton onClick={onClose} testid="close-prefs" />
        </div>
        <div className="px-4 py-4 space-y-6">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold mb-2">Universal defaults</p>
            <div className="space-y-2">
              {sections.universal.map(f => (
                <Collapsible key={f.field_id} label={f.label} tone="rose"
                  summary={(() => { const v = universal[f.field_id]; return Array.isArray(v) && v.length ? `${v.length} set` : "None"; })()}
                  testid={`collapse-${f.field_id}`}>
                  {renderField(f)}
                </Collapsible>
              ))}
            </div>
          </div>
          {["canvas", "look", "faves", "notes"].map(sect => sections[sect]?.length > 0 && (
            <div key={sect}>
              <p className="text-[10px] uppercase tracking-wider text-stone-700 font-bold mb-2">{sect === "faves" ? "Products & faves" : sect.charAt(0).toUpperCase() + sect.slice(1)}</p>
              <div className="space-y-4">{sections[sect].map(f => <div key={f.field_id}>{renderField(f)}</div>)}</div>
            </div>
          ))}
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 px-4 py-3" style={{ maxWidth: TOKENS.MAX_W, left: "50%", transform: "translateX(-50%)" }}>
          <Btn full tone="ink" icon={Save} onClick={savePrefs} disabled={saving} testid="save-prefs">{saving ? "Saving..." : "Save preferences"}</Btn>
        </div>
      </div>
    </div>
  );
}

function TextMultiInput({ f, val, setBag }) {
  const [draft, setDraft] = useState("");
  const items = Array.isArray(val) ? val : [];
  const add = () => {
    if (!draft.trim()) return;
    setBag(prev => ({ ...prev, [f.field_id]: [...(Array.isArray(prev[f.field_id]) ? prev[f.field_id] : []), draft.trim()] }));
    setDraft("");
  };
  const rm = (i) => setBag(prev => ({ ...prev, [f.field_id]: (prev[f.field_id] || []).filter((_, j) => j !== i) }));
  return (
    <div className="space-y-1">
      <FieldLabel>{f.label}</FieldLabel>{f.hint && <HintText>{f.hint}</HintText>}
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            {it}<button onClick={() => rm(i)} data-testid={`pref-${f.field_id}-remove-${i}`}><Trash2 className="w-2.5 h-2.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5 mt-1">
        <div className="flex-1"><TextInput value={draft} onChange={setDraft} placeholder="Add one and click Add" testid={`pref-${f.field_id}-input`} /></div>
        <Btn size="sm" tone="outline" icon={Plus} onClick={add} testid={`pref-${f.field_id}-add`}>Add</Btn>
      </div>
    </div>
  );
}

function InspoDrawer({ inspo, onClose, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        form.append("category", "makeup");
        await axios.post(`${API}/phygital/me/inspo`, form, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      }
      await onSaved();
      toast.success("Inspo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const remove = async (id) => {
    try { await axios.delete(`${API}/phygital/me/inspo/${id}`, { headers: authHeaders() }); await onSaved(); } catch { /* ignore */ }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" data-testid="inspo-drawer">
      <div className={`${TOKENS.FRAME} bg-white rounded-t-3xl max-h-[92vh] overflow-y-auto pb-24 relative`}>
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between border-b border-stone-100 px-4 h-14">
          <p className="text-sm font-bold">Inspiration board</p>
          <CloseButton onClick={onClose} testid="close-inspo" />
        </div>
        <div className="px-4 py-4 space-y-4">
          <label className="block rounded-2xl border-2 border-dashed border-stone-300 hover:border-purple-500 p-6 text-center cursor-pointer" data-testid="upload-inspo">
            <Upload className="w-6 h-6 text-stone-500 mx-auto mb-1" />
            <p className="text-sm font-semibold text-stone-900">Select to upload from device</p>
            <p className="text-[11px] text-stone-500 mt-0.5">{uploading ? "Uploading..." : "PNG, JPG, HEIC up to 10MB"}</p>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="upload-input" />
          </label>
          <div className="grid grid-cols-3 gap-2">
            {inspo.map(i => (
              <div key={i.inspo_id} className="relative group aspect-square rounded-xl overflow-hidden border border-stone-200">
                <img src={i.url.startsWith("http") ? i.url : `${process.env.REACT_APP_BACKEND_URL}${i.url}`} alt="inspo" className="w-full h-full object-cover" />
                <button onClick={() => remove(i.inspo_id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3 h-3 text-rose-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 px-4 py-3" style={{ maxWidth: TOKENS.MAX_W, left: "50%", transform: "translateX(-50%)" }}>
          <Btn full tone="ink" onClick={onClose} testid="done-inspo">Done</Btn>
        </div>
      </div>
    </div>
  );
}

function HostFormDrawer({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", brand: "", address: "", phone: "", instagram: "", price_low: "", price_high: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Host name required"); return; }
    setSaving(true);
    try {
      const body = { ...f, price_low: f.price_low ? Number(f.price_low) : null, price_high: f.price_high ? Number(f.price_high) : null, category: "makeup" };
      const r = await axios.post(`${API}/phygital/me/hosts`, body, { headers: authHeaders() });
      toast.success("Host added");
      if (r.data.host) onSaved(r.data.host);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" data-testid="host-drawer">
      <div className={`${TOKENS.FRAME} bg-white rounded-t-3xl max-h-[92vh] overflow-y-auto pb-24 relative`}>
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between border-b border-stone-100 px-4 h-14">
          <p className="text-sm font-bold">Add a new host</p>
          <CloseButton onClick={onClose} testid="close-host" />
        </div>
        <div className="px-4 py-4 space-y-3">
          <div><FieldLabel>Name *</FieldLabel><TextInput value={f.name} onChange={(v) => setF({ ...f, name: v })} placeholder="Shelsea Patton" testid="hf-name" /></div>
          <div><FieldLabel>Brand name</FieldLabel><TextInput value={f.brand} onChange={(v) => setF({ ...f, brand: v })} placeholder="Privé Beauté" testid="hf-brand" /></div>
          <div><FieldLabel>Address</FieldLabel><GooglePlacesAutocomplete value={f.address} onInputChange={(v) => setF({ ...f, address: v })} onChange={(p) => setF({ ...f, address: p?.address || p?.name || f.address })} placeholder="123 Main St, Dallas TX" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><FieldLabel>Phone</FieldLabel><TextInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })} placeholder="469 555 1234" testid="hf-phone" /></div>
            <div><FieldLabel>Instagram</FieldLabel><TextInput value={f.instagram} onChange={(v) => setF({ ...f, instagram: v })} placeholder="@handle" testid="hf-ig" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><FieldLabel>Price low ($)</FieldLabel><TextInput type="number" value={f.price_low} onChange={(v) => setF({ ...f, price_low: v })} placeholder="150" testid="hf-plow" /></div>
            <div><FieldLabel>Price high ($)</FieldLabel><TextInput type="number" value={f.price_high} onChange={(v) => setF({ ...f, price_high: v })} placeholder="295" testid="hf-phigh" /></div>
          </div>
        </div>
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-stone-200 px-4 py-3" style={{ maxWidth: TOKENS.MAX_W, left: "50%", transform: "translateX(-50%)" }}>
          <Btn full tone="ink" icon={Save} onClick={save} disabled={saving} testid="save-host">{saving ? "Saving..." : "Save host"}</Btn>
        </div>
      </div>
    </div>
  );
}
